import { type Dtos, FeatureFlagName } from "@inrev/common";
import { useFeatureFlags } from "@portal/api/feature-flags";
import type { BannerTone } from "@portal/components/primitives/Banner";
import { createContext, type ReactNode, useContext, useMemo } from "react";

type BannerAudience = "admin" | "agent" | "all";

export type SystemBannerEntry = {
	id: string;
	message: ReactNode;
	tone: BannerTone;
	audience: BannerAudience;
};

type SystemBannerContextValue = {
	banners: SystemBannerEntry[];
};

const SystemBannerContext = createContext<SystemBannerContextValue>({ banners: [] });

function featureFlagsToBanners(flags: Dtos.FeatureFlag.GetAll.Response): SystemBannerEntry[] {
	const entries: SystemBannerEntry[] = [];

	if (flags.find((f) => f.name === FeatureFlagName.account_auto_approval)?.enabled === false) {
		entries.push({
			id: "account_auto_approval_paused",
			message:
				"Account auto-approval is currently disabled. All account submissions will require manual review.",
			tone: "notification",
			audience: "admin",
		});
	}

	if (flags.find((f) => f.name === FeatureFlagName.bond_request_auto_approval)?.enabled === false) {
		entries.push({
			id: "bond_request_auto_approval_paused",
			message:
				"Bond request auto-approval is currently disabled. All bond request submissions will require manual review.",
			tone: "notification",
			audience: "admin",
		});
	}

	return entries;
}

type SystemBannerProviderProps = {
	audience: "admin" | "agent";
	children: ReactNode;
};

export const SystemBannerProvider = ({ audience, children }: SystemBannerProviderProps) => {
	const { featureFlags } = useFeatureFlags();

	const banners = useMemo<SystemBannerEntry[]>(() => {
		// Feature flags are prefetched before this provider mounts (AdminPrefetchProvider /
		// AgentPrefetchProvider). This guard covers hard-refresh races or cache misses.
		if (!featureFlags) return [];
		return featureFlagsToBanners(featureFlags).filter(
			(e) => e.audience === "all" || e.audience === audience,
		);
	}, [featureFlags, audience]);

	return (
		<SystemBannerContext.Provider value={{ banners }}>{children}</SystemBannerContext.Provider>
	);
};

export const useSystemBanners = () => useContext(SystemBannerContext);
